import React, {useState, useEffect} from 'react';
import {Box, Dialog, Typography, InputBase} from '@mui/material';
import {useTranslate} from 'react-admin';
import {t} from '../../themes/statamicTokens';
import PrimaryButton from './PrimaryButton';

/**
 * Modal for approving / rejecting a post (or a batch of posts).
 *
 * Props:
 *   open       — boolean, dialog visibility
 *   action     — 'approve' | 'reject'
 *   onClose    — called to dismiss
 *   onConfirm  — (reason: string) => void; reason is empty string when approving
 *   pending    — disables buttons and hides close-on-backdrop while in flight
 *   bulkCount  — optional; when > 1, copy becomes "Approve N posts" / "Reject N posts"
 */
const ApprovalDialog = ({open, action, onClose, onConfirm, pending, bulkCount}) => {
    const translate = useTranslate();
    const [reason, setReason] = useState('');
    const isApprove = action === 'approve';
    const isBulk = typeof bulkCount === 'number' && bulkCount > 1;

    useEffect(() => {
        if (!open) setReason('');
    }, [open]);

    const title = isApprove
        ? (isBulk ? `${translate('Approve')} ${bulkCount} ${translate('posts')}` : translate('Approve Post'))
        : (isBulk ? `${translate('Reject')} ${bulkCount} ${translate('posts')}` : translate('Reject Post'));

    const body = isApprove
        ? (isBulk
            ? translate('Are you sure you want to approve these posts? They will be visible to all guardians.')
            : translate('Are you sure you want to approve this post? It will be visible to all guardians.'))
        : (isBulk
            ? translate('This reason will be applied to every selected post.')
            : translate('Please provide a reason for rejecting this post.'));

    const confirmLabel = isApprove ? translate('Approve') : translate('Reject');

    return (
        <Dialog
            open={open}
            onClose={pending ? undefined : onClose}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: `${t.radius.lg}px`,
                        border: `1px solid ${t.color.border.default}`,
                        boxShadow: t.shadow.modal,
                        maxWidth: 480,
                    },
                },
            }}
        >
            <Box sx={{p: 3}}>
                <Typography
                    sx={{
                        fontSize: `${t.type.h3.size}px`,
                        fontWeight: t.type.h3.weight,
                        color: t.color.text.primary,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        mt: 1,
                        fontSize: `${t.type.small.size}px`,
                        color: t.color.text.secondary,
                        lineHeight: 1.5,
                    }}
                >
                    {body}
                </Typography>

                {!isApprove && (
                    <Box
                        sx={{
                            mt: 2,
                            px: 1.5,
                            py: 1,
                            borderRadius: `${t.radius.sm}px`,
                            border: `1px solid ${t.color.border.default}`,
                            backgroundColor: t.color.surface,
                            '&:focus-within': {
                                borderColor: t.color.accent.primary,
                                boxShadow: `0 0 0 3px ${t.color.accent.primaryTint}`,
                            },
                        }}
                    >
                        <InputBase
                            multiline
                            rows={3}
                            autoFocus
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={translate('Rejection Reason')}
                            sx={{
                                width: '100%',
                                fontSize: `${t.type.body.size}px`,
                                color: t.color.text.primary,
                            }}
                        />
                    </Box>
                )}

                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3}}>
                    <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>
                        {translate('Cancel')}
                    </PrimaryButton>
                    <PrimaryButton
                        variant={isApprove ? 'primary' : 'danger'}
                        onClick={() => onConfirm(reason)}
                        disabled={pending || (!isApprove && !reason.trim())}
                    >
                        {pending ? '…' : confirmLabel}
                    </PrimaryButton>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ApprovalDialog;
